import { ChainId } from '@pancakeswap/sdk'
import BigNumber from 'bignumber.js'
import uniq from 'lodash/uniq'
import fromPairs from 'lodash/fromPairs'
import _isNil from "lodash/isNil";

import { getNftPoolsConfig } from '../constants'
import smartChefNftABI from '../abis/ISmartChefNFT.json'
import erc721ABI from '../abis/IERC721.json'
import { OnChainProvider, SerializedNftPool } from '../types'

const getNftPools = (chainId: ChainId) => {
  const poolsConfig = getNftPoolsConfig(chainId)
  if (!poolsConfig) {
    throw new Error(`Unable to get pools config on chain ${chainId}`)
  }
  return poolsConfig
}

interface FetchUserDataParams {
  account: string
  chainId: ChainId
  provider: OnChainProvider
}

export const fetchNftPoolsIsApprovedForAll = async ({ account, chainId, provider }: FetchUserDataParams) => {
  const nftPools = getNftPools(chainId)
  const calls = nftPools.map(({ contractAddress, stakingToken }) => ({
    abi: erc721ABI,
    address: stakingToken.address,
    functionName: 'isApprovedForAll',
    args: [account, contractAddress],
  }))

  const client = provider({ chainId })
  const isApprovedForAlls = (
    await client.multicall({
      // @ts-ignore
      contracts: calls,
      allowFailure: false,
    })
  )

  return fromPairs(nftPools.map((pool, index) => [pool.sousId, isApprovedForAlls[index]]))
}

export const fetchUserNftBalances = async ({ account, chainId, provider }: FetchUserDataParams) => {
  const nftPoolsV2 = getNftPools(chainId).filter((p) => p.version === 2)
  const tokensV2 = uniq(nftPoolsV2.map((pool) => pool.stakingToken.address))

  const tokenBalanceCalls = tokensV2.map((token) => ({
    abi: erc721ABI,
    address: token,
    functionName: 'balanceOf',
    args: [account],
  }))

  const client = provider({ chainId })
  const tokenBalancesRaw = await client.multicall({
    // @ts-ignore
    contracts: tokenBalanceCalls,
    allowFailure: false,
  })

  const tokenBalances = fromPairs(
    await Promise.all(
      tokensV2.map(async (token, index) => {
        const tokenIdsV2Calls = []
        // @ts-ignore
        for (let i = 0; i < tokenBalancesRaw[index]; i++) {
          tokenIdsV2Calls.push({
            abi: erc721ABI,
            address: token,
            functionName: 'tokenOfOwnerByIndex',
            args: [account, i],
          })
        }
        const tokenIdsV2Raw = await client.multicall({
          // @ts-ignore
          contracts: tokenIdsV2Calls,
          allowFailure: false,
        })
        const tokenIdsV2AsString = tokenIdsV2Raw.map((id: any) => id.toString())

        return [token, tokenIdsV2AsString]
      }),
    ),
  )

  const poolTokenBalances = fromPairs(
    nftPoolsV2
      .map<[number, string[]] | null>((pool) => {
        if (!tokenBalances[pool.stakingToken.address]) return null
        return [pool.sousId, tokenBalances[pool.stakingToken.address]]
      })
      .filter((p): p is [number, string[]] => Boolean(p)),
  )

  const nftPoolsV1 = getNftPools(chainId).filter((p) => p.version === 1)
  const tokensV1 = uniq(nftPoolsV1.map((pool) => pool.stakingToken.address))

  const totalSupplyCalls = tokensV1.map((token) => ({
    abi: erc721ABI,
    address: token,
    functionName: 'totalSupply',
  }))

  const totalSuppliesRaw = await client.multicall({
    // @ts-ignore
    contracts: totalSupplyCalls,
    allowFailure: false,
  })
  const totalSupplies: { [address: string]: number } = fromPairs(
    totalSuppliesRaw.map((supply, index) => [tokensV1[index], Number(supply.toString())]),
  )
  // Create 'ownerOf' calls for all the v1 tokens
  const ownerOfCalls: any = tokensV1.flatMap((token) =>
    Array.from({ length: totalSupplies[token] }, (_, i) => ({
      abi: erc721ABI,
      address: token,
      functionName: 'ownerOf',
      args: [i + 1], // tokenId starts from 1
    })),
  )

  // Fetch the owners of all the v1 tokens
  const ownersRaw = await client.multicall({
    // @ts-ignore
    contracts: ownerOfCalls,
    allowFailure: false,
  })

  // Map the owners to their corresponding tokens
  const ownedTokens = ownersRaw.map((owner, index) =>
    owner.toString().toLowerCase() === account.toLowerCase() ? ownerOfCalls[index].args[0] : undefined,
  )

  const poolTokenBalancesV1 = fromPairs(
    // @ts-ignore
    nftPoolsV1.map<[number, number[]] | null>((pool) => {
        // Find the token IDs for this pool's staking token that belong to the account
        const tokenIdsForThisPool = ownedTokens.filter(
          (tokenId) => !_isNil(tokenId) && (ownerOfCalls[tokenId - 1].address === pool.stakingToken.address)
        )

        if (tokenIdsForThisPool.length === 0) return null

        return [pool.sousId, tokenIdsForThisPool.map(String)]
      })
      .filter((p): p is [number, number[]] => Boolean(p)),
  )

  return { ...poolTokenBalances, ...poolTokenBalancesV1 }
}

export const fetchUserStakedNftBalances = async ({ account, chainId, provider }: FetchUserDataParams) => {
  const nftPools = getNftPools(chainId)
  const calls = nftPools.map(({ contractAddress }) => ({
    abi: smartChefNftABI,
    address: contractAddress,
    functionName: 'getUserTokenIds',
    args: [account],
  }))
  const client = provider({ chainId })
  const userTokenIds = await client.multicall({
    // @ts-ignore
    contracts: calls,
    allowFailure: false,
  })
  const userTokenIdsAsString: string[][] = []
  // @ts-ignore
  userTokenIds.forEach((idArray: number[]) => {
    userTokenIdsAsString.push(idArray.map((id: number) => id.toString()))
  })
  return fromPairs(
    nftPools.map<[number, string[]] | any[]>((pool, index) => [pool.sousId, userTokenIdsAsString[index]]),
  )
}

export const fetchUserPendingRewards = async ({ account, chainId, provider }: FetchUserDataParams) => {
  const nftPools = getNftPools(chainId)
  const calls = nftPools.map(({ contractAddress }) => ({
    abi: smartChefNftABI,
    address: contractAddress,
    functionName: 'pendingReward',
    args: [account],
  }))
  const client = provider({ chainId })
  const res = await client.multicall({
    // @ts-ignore
    contracts: calls,
    allowFailure: false,
  })
  // @ts-ignore
  return fromPairs(nftPools.map((pool, index) => [pool.sousId, new BigNumber(res[index]).toJSON()]))
}
